import { styled } from 'styled-components';
import type { JSX } from 'react';
import { useEffect, useMemo } from 'react';
import { mq, Button, Icon, useThemeColor, useTheme } from '@wistia/vhs';
import { trackStructEvent } from '~/utilities/snowplow';
import type { ApolloQueryResult, DocumentNode } from '@apollo/client';
import type { CommentForMedia, PublicCommentsSelection, TeamCommentForMedia } from '~/graphql';
import { CommentingHasMovedBanner } from '~/singlePageApp/screens/MediaPreview/customize/sections/section_components/CommentingHasMovedBanner';
import { useSearchParams } from 'react-router';
import { CommentsAndForm } from './CommentsAndForm';
import { ButtonTabs } from './ButtonTabs';
import type { ContactData } from './types';
import type { CommentingViewsType } from './CommentContext';
import { CommentContextProvider, commentingViews, useCommentContext } from './CommentContext';
import { PublicCommentsChooser } from './PublicCommentsChooser';
import { CommentsSortChooser } from './CommentsSortChooser';
import type { MediaQuery } from '../__gql/Media.gql.gen';
import { MoreAudienceCommentsDocument, MoreTeamCommentsDocument } from './__gql/Comments.gql.gen';
import { flattenCommentEdges } from '../utils/flattenCommentEdges';
import { STICKY_TAB_BAR_WRAPPER_HEIGHT } from '../MediaLayoutComponents';
import { MediaStoreSelectors, useMediaStore } from '../Store/MediaStore';

const Container = styled.div`
  height: 100%;
`;

const GridContainer = styled.div<{ isRightOfPlayer: boolean }>`
  display: grid;
  grid-template-rows: max-content 1fr;
  height: 100%;
  max-width: 70ch;
  row-gap: ${({ theme, isRightOfPlayer }) => (isRightOfPlayer ? theme.spacing.space04 : '0')};
`;

const CommentOptions = styled.div<{
  isRightOfPlayer: boolean;
  rowGap: string;
  showPublicCommentsChooser: boolean;
}>`
  display: grid;
  grid-template-areas: 'Tabs Chooser SortAndFilter';
  grid-template-columns: ${({ isRightOfPlayer }) => (isRightOfPlayer ? '100%' : '1fr 2fr 2fr 1fr')};
  margin: ${({ showPublicCommentsChooser, isRightOfPlayer }) => {
    if (showPublicCommentsChooser) {
      return '16px 0 0 0';
    }
    return isRightOfPlayer ? '0' : '0 16px';
  }};
  row-gap: ${({ rowGap }) => rowGap};

  ${({ isRightOfPlayer }) => {
    if (isRightOfPlayer) {
      return `grid-template-areas: 'Chooser' 'Tabs' 'SortAndFilter';`;
    }
    return '';
  }}

  ${mq.xsAndUp} {
    grid-template-areas: 'Chooser' 'Tabs' 'SortAndFilter';
    grid-template-columns: 100%;
  }
`;

const StickyOptionsWrapper = styled.div<{
  isRightOfPlayer: boolean;
  stickyOptionsWrapperTop: string;
}>`
  background-color: ${({ theme, isRightOfPlayer }) =>
    isRightOfPlayer ? '#fff' : theme.color.grey100};
  position: ${({ isRightOfPlayer }) => (isRightOfPlayer ? '' : 'sticky')};
  top: ${({ stickyOptionsWrapperTop }) => stickyOptionsWrapperTop};
  padding-bottom: 4px;
`;

// Typical heights of the `StickyOptionsWrapper` - used to calculate top offset for other
// sticky elements / update if this element's children are updated such that the height changes
// TODO we may need to change sticky options wrapper height to account for the 3rd row being added (bigger height)
const STICKY_OPTIONS_WRAPPER_HEIGHT = 58;
const STICKY_OPTIONS_WRAPPER_HEIGHT_TEAM_WITH_TABS = 102;
const MOBILE_STICKY_OPTIONS_WRAPPER_HEIGHT_TEAM = 106;
const MOBILE_STICKY_OPTIONS_WRAPPER_HEIGHT_TEAM_NO_TABS = 44;
const MOBILE_STICKY_OPTIONS_WRAPPER_HEIGHT_AUDIENCE = 72;

export const getStickyOptionsWrapperHeight = (
  commentingView: CommentingViewsType,
  showTabs: boolean,
  isMobile: boolean,
): number => {
  if (isMobile) {
    if (commentingView === commentingViews.audience) {
      return MOBILE_STICKY_OPTIONS_WRAPPER_HEIGHT_AUDIENCE;
    }
    return showTabs
      ? MOBILE_STICKY_OPTIONS_WRAPPER_HEIGHT_TEAM
      : MOBILE_STICKY_OPTIONS_WRAPPER_HEIGHT_TEAM_NO_TABS;
  }
  return showTabs && commentingView === commentingViews.team
    ? STICKY_OPTIONS_WRAPPER_HEIGHT_TEAM_WITH_TABS
    : STICKY_OPTIONS_WRAPPER_HEIGHT;
};

export type CommentTabName = 'Audience' | 'Team';

const Comments = ({
  contact,
  mediaId,
  mediaHashedId,
  topLevelComments,
  isRightOfPlayer,
  parentId,
  fetchMore,
  queryDocument,
}: {
  contact: ContactData | null | undefined;
  mediaId: string;
  mediaHashedId: string;
  topLevelComments:
    | NonNullable<MediaQuery['media']>['topLevelAudienceComments']
    | NonNullable<MediaQuery['media']>['topLevelTeamComments'];
  parentId: string | null;
  isRightOfPlayer: boolean;
  fetchMore: (arg: GenericRecord) => Promise<ApolloQueryResult<MediaQuery>>;
  queryDocument: DocumentNode;
}): JSX.Element => {
  const commentingSort = useMediaStore(MediaStoreSelectors.getCommentingSort);
  const hideResolvedComments = useMediaStore(MediaStoreSelectors.getHideResolvedComments);

  const {
    pageInfo: { endCursor, hasNextPage },
    edges = [],
  } = topLevelComments;

  const loadMoreComments = () => {
    void fetchMore({
      query: queryDocument,
      variables: {
        cursor: endCursor,
        mediaHashedId,
        orderBy: commentingSort,
        hideResolved: hideResolvedComments,
      },
    });
  };

  const comments = flattenCommentEdges(edges as (CommentForMedia | TeamCommentForMedia)[]);

  return (
    <CommentsAndForm
      contact={contact}
      mediaId={mediaId}
      comments={comments}
      isRightOfPlayer={isRightOfPlayer}
      parentId={parentId}
      loadMoreFunction={loadMoreComments}
      hasNextPage={hasNextPage}
    />
  );
};

const StyledButton = styled(Button)`
  grid-area: SortAndFilter;
  justify-self: end;
`;

export const ToggleResolvedCommentsButton = ({
  setHideResolvedComments,
  hideResolvedComments,
}: {
  setHideResolvedComments: (arg: boolean) => void;
  hideResolvedComments: boolean;
}): JSX.Element => {
  const [grey500] = useThemeColor(['grey500']);

  return (
    <StyledButton
      variant="tertiary"
      onClick={() => {
        setHideResolvedComments(!hideResolvedComments);
      }}
      icon={
        <Icon
          type="checkmark-circle-outline"
          color={grey500}
        />
      }
      iconPosition="end"
      label={`${hideResolvedComments ? 'Show' : 'Hide'} resolved`}
      size="md"
    />
  );
};

export const TabsContent = ({
  activeTab,
  topLevelAudienceComments,
  contact,
  mediaId,
  mediaHashedId,
  topLevelTeamComments,
  isRightOfPlayer,
  fetchMore,
}: {
  activeTab: CommentTabName;
  topLevelAudienceComments: NonNullable<MediaQuery['media']>['topLevelAudienceComments'];
  contact: ContactData | null | undefined;
  mediaId: string;
  mediaHashedId: string;
  topLevelTeamComments: NonNullable<MediaQuery['media']>['topLevelTeamComments'];
  isRightOfPlayer: boolean;
  fetchMore: (arg: GenericRecord) => Promise<ApolloQueryResult<MediaQuery>>;
}): JSX.Element => {
  const { setCommentingView } = useCommentContext();
  const audienceCommentsSelected = activeTab === 'Audience';

  useEffect(() => {
    if (audienceCommentsSelected) {
      setCommentingView(commentingViews.audience);
    } else {
      setCommentingView(commentingViews.team);
    }
  }, [audienceCommentsSelected, setCommentingView]);

  const parentId = audienceCommentsSelected ? mediaId : null;
  const topLevelComments = audienceCommentsSelected
    ? topLevelAudienceComments
    : topLevelTeamComments;
  const queryDocument = audienceCommentsSelected
    ? MoreAudienceCommentsDocument
    : MoreTeamCommentsDocument;

  return (
    <Comments
      contact={contact}
      mediaId={mediaId}
      mediaHashedId={mediaHashedId}
      topLevelComments={topLevelComments}
      isRightOfPlayer={isRightOfPlayer}
      parentId={parentId}
      fetchMore={fetchMore}
      queryDocument={queryDocument}
    />
  );
};

const MaybePublicCommentsChooser = ({
  canUpdatePublicComments,
  mediaId,
  publicCommentsSelection,
  projectIsLocked,
  isRightOfPlayer,
}: {
  canUpdatePublicComments: boolean;
  mediaId: string;
  publicCommentsSelection: PublicCommentsSelection;
  projectIsLocked: boolean;
  isRightOfPlayer: boolean;
}): JSX.Element | JSX.Element[] | null => {
  if (!canUpdatePublicComments) {
    return null;
  }
  if (publicCommentsSelection === 'team') {
    return (
      <CommentingHasMovedBanner>
        <PublicCommentsChooser
          mediaId={mediaId}
          initialCommentSelection={publicCommentsSelection}
          projectIsLocked={projectIsLocked}
          isRightOfPlayer={isRightOfPlayer}
        />
      </CommentingHasMovedBanner>
    );
  }
  return null;
};

const CommentOptionsSection = ({
  isRightOfPlayer,
  canUpdatePublicComments,
  mediaId,
  publicCommentsSelection,
  activeCommentTab,
  onTabSelected,
  projectIsLocked,
  showCommentTabs,
}: {
  isRightOfPlayer: boolean;
  canUpdatePublicComments: boolean;
  mediaId: string;
  publicCommentsSelection: PublicCommentsSelection;
  activeCommentTab: CommentTabName;
  onTabSelected: (tab: CommentTabName) => void;
  projectIsLocked: boolean;
  showCommentTabs: boolean;
}): JSX.Element => {
  const { isAnonymousMedia } = useCommentContext();
  const teamCommentsTabSelected = activeCommentTab === 'Team';
  const stickyPlayerPhantomHeight = useMediaStore(MediaStoreSelectors.getStickyPlayerPhantomHeight);
  const playerStickyPosition = useMediaStore(MediaStoreSelectors.getPlayerStickyPosition);
  const hideResolvedComments = useMediaStore(MediaStoreSelectors.getHideResolvedComments);
  const { setHideResolvedComments } = useMediaStore(MediaStoreSelectors.getActions);
  const { spacing } = useTheme();

  const stickyOptionsWrapperTop = useMemo(() => {
    if (isRightOfPlayer) {
      return 0;
    }
    if (playerStickyPosition === 'top') {
      return stickyPlayerPhantomHeight + STICKY_TAB_BAR_WRAPPER_HEIGHT;
    }

    return STICKY_TAB_BAR_WRAPPER_HEIGHT;
  }, [isRightOfPlayer, playerStickyPosition, stickyPlayerPhantomHeight]);

  // The comment options section requires different row-gap
  // values when different options are or are not visible
  // in order to remain consistent with the designs.
  let rowGap = '';
  if (canUpdatePublicComments && showCommentTabs) {
    rowGap = spacing.space04;
  } else if (canUpdatePublicComments && !showCommentTabs) {
    rowGap = spacing.space02;
  } else {
    rowGap = '0px';
  }

  return (
    <StickyOptionsWrapper
      stickyOptionsWrapperTop={`${stickyOptionsWrapperTop}px`}
      isRightOfPlayer={isRightOfPlayer}
    >
      <CommentOptions
        isRightOfPlayer={isRightOfPlayer}
        showPublicCommentsChooser={canUpdatePublicComments && publicCommentsSelection === 'team'}
        rowGap={rowGap}
      >
        <MaybePublicCommentsChooser
          canUpdatePublicComments={canUpdatePublicComments}
          mediaId={mediaId}
          publicCommentsSelection={publicCommentsSelection}
          projectIsLocked={projectIsLocked}
          isRightOfPlayer={isRightOfPlayer}
        />
        {showCommentTabs ? (
          <ButtonTabs
            isRightOfPlayer={isRightOfPlayer}
            tabNames={['Team', 'Audience']}
            activeTab={activeCommentTab}
            onTabSelected={onTabSelected}
          />
        ) : null}
        {teamCommentsTabSelected && !isAnonymousMedia ? <CommentsSortChooser /> : null}
        {teamCommentsTabSelected && !isAnonymousMedia ? (
          <ToggleResolvedCommentsButton
            hideResolvedComments={hideResolvedComments}
            setHideResolvedComments={setHideResolvedComments}
          />
        ) : null}
      </CommentOptions>
    </StickyOptionsWrapper>
  );
};

export const CommentTabs = ({
  activeCommentId,
  activeCommentTab,
  contact,
  isRightOfPlayer,
  setActiveCommentTab,
  onCommentClick,
  media,
  fetchMore,
}: {
  activeCommentId?: string;
  activeCommentTab: CommentTabName;
  contact: ContactData | null | undefined;
  isRightOfPlayer: boolean;
  setActiveCommentTab: (tab: CommentTabName) => void;
  onCommentClick?: (commentId: string, timestamp?: number) => void;
  media: NonNullable<MediaQuery['media']>;
  fetchMore: (arg: GenericRecord) => Promise<ApolloQueryResult<MediaQuery>>;
}): JSX.Element | null => {
  const [searchParams] = useSearchParams();
  const {
    id: mediaId,
    hashedId,
    project,
    topLevelAudienceComments,
    topLevelTeamComments,
    publicCommentsSelection,
    canUpdatePublicComments,
  } = media;

  const { projectAuthorizedContacts, isLocked: projectIsLocked } = project ?? {};

  const showCommentTabs =
    Number(topLevelAudienceComments.edges?.length) > 0 || publicCommentsSelection === 'audience';

  // When audience comments are disabled in the Media preview, the audience tab is only displayed if there are any audience comments.
  // If all the audience comments are deleted, this useEffect ensures that the active tab is set back to
  // 'Team' because the audience tab will be hidden.
  useEffect(() => {
    if (
      Number(topLevelAudienceComments.edges?.length) === 0 &&
      publicCommentsSelection !== 'audience'
    ) {
      setActiveCommentTab('Team');
    }
  }, [topLevelAudienceComments, setActiveCommentTab, publicCommentsSelection]);

  // Read audience_tab parameter on mount and when returning to Comments tab
  useEffect(() => {
    if (searchParams.get('audience_tab') === 'true' && showCommentTabs) {
      setActiveCommentTab('Audience');
    }
  }, [setActiveCommentTab, showCommentTabs, searchParams]);

  const onTabSelected = (tab: CommentTabName) => {
    if (tab === activeCommentTab) {
      return;
    }

    setActiveCommentTab(tab);

    // Update URL parameters based on tab selection
    if (tab === 'Audience') {
      searchParams.set('audience_tab', 'true');
    } else {
      searchParams.delete('audience_tab');
    }
    window.history.replaceState(null, '', `${window.location.pathname}?${searchParams.toString()}`);

    trackStructEvent({
      category: 'button',
      action: 'click',
      label: `media-page-${tab.toLowerCase()}-comment-tab`,
    });
  };

  return (
    <CommentContextProvider
      activeCommentId={activeCommentId}
      onCommentClick={onCommentClick}
      defaultCommentingView={activeCommentTab}
      isAnonymousMedia={false}
      contactsForTeamTags={projectAuthorizedContacts?.nodes}
      shouldUseLegacyPlayerApi={true}
      showCommentTabs={showCommentTabs}
    >
      <Container>
        <GridContainer isRightOfPlayer={isRightOfPlayer}>
          <CommentOptionsSection
            isRightOfPlayer={isRightOfPlayer}
            canUpdatePublicComments={canUpdatePublicComments}
            mediaId={mediaId}
            publicCommentsSelection={publicCommentsSelection}
            activeCommentTab={activeCommentTab}
            onTabSelected={onTabSelected}
            projectIsLocked={Boolean(projectIsLocked)}
            showCommentTabs={showCommentTabs}
          />
          <TabsContent
            activeTab={activeCommentTab}
            topLevelAudienceComments={topLevelAudienceComments}
            contact={contact}
            mediaId={mediaId}
            mediaHashedId={hashedId}
            topLevelTeamComments={topLevelTeamComments}
            isRightOfPlayer={isRightOfPlayer}
            fetchMore={fetchMore}
          />
        </GridContainer>
      </Container>
    </CommentContextProvider>
  );
};
